import { ArrowUpRight } from "lucide-react";
import { APPS } from "@/data/apps";
import { useHive } from "@/store/useHive";
import { appState, isProductApp } from "@/lib/entitlements";
import { ProductPreviewContent } from "@/components/products/ProductPreview";
import { externalAppUrl, openExternalApp } from "@/lib/appLinks";
import type { AppId } from "@/types";

/**
 * Real-product boundary. The Momentum products run as their own apps and sign
 * in through Hive SSO. What renders here depends on the tenant's entitlement
 * for the linked product (from the session payload — never frontend constants):
 *
 *  - enabled + URL configured  → "Open {product}" launcher
 *  - enabled, no URL           → honest "not connected" note for operators
 *  - preview / locked          → Early-Access preview: capability blurb +
 *                                "Request early access" demand capture
 */
export function ProductGate({
  appId,
  linkAppId,
  note,
}: {
  appId: AppId;
  /** App whose configured URL powers this surface (defaults to appId). */
  linkAppId?: AppId;
  note?: string;
}) {
  const app = APPS[appId];
  const link = linkAppId ?? appId;
  const state = useHive((s) => appState(s.entitlements, link));

  // Not enabled → this window is an Early-Access preview surface.
  if (isProductApp(link) && state !== "enabled") {
    return (
      <div className="flex h-full min-h-[360px] flex-col items-center justify-center p-8">
        <ProductPreviewContent product={link} />
      </div>
    );
  }

  const url = externalAppUrl(link);
  const envVar = `VITE_${link.toUpperCase()}_URL`;

  return (
    <div className="flex h-full min-h-[360px] flex-col items-center justify-center gap-3 p-8 text-center">
      <div
        className="grid h-14 w-14 place-items-center rounded-2xl text-white shadow-lg"
        style={{ background: `linear-gradient(135deg, ${app.accent}, ${app.accent}aa)` }}
      >
        <app.icon size={26} />
      </div>
      <div className="text-base font-semibold text-app">{app.name}</div>
      <p className="max-w-sm text-xs leading-relaxed text-app-2">
        {note ?? `${app.name} runs as its own product and signs in with your Hive account.`}
      </p>
      {url ? (
        <button
          onClick={() => openExternalApp(url)}
          className="mt-2 flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold text-black transition hover:brightness-105"
          style={{ background: app.accent }}
        >
          Open {app.name} <ArrowUpRight size={13} />
        </button>
      ) : (
        <p className="max-w-sm text-xs leading-relaxed text-app-2">
          Not connected in this deployment — an administrator can set{" "}
          <code className="rounded bg-surface-2 px-1 py-0.5 text-[11px]">{envVar}</code> to enable
          launching it from here.
        </p>
      )}
    </div>
  );
}
